<template>
  <div class="strain-calories-card">
    <div class="chart-wrap">
      <canvas ref="chartCanvas"></canvas>
    </div>

    <div class="bottom-legend">
      <button type="button"  class="legend-item clickable"
        :class="{ disabled: !visibleSeries.strain }" @click="toggleSeries('strain')" >
        <span class="swatch strain-box"></span>
        <div>
          <div class="legend-label">Strain (Actual)</div>
          <div class="legend-sub">Your daily strain - Avg: {{ avgStrain }}</div>
        </div>
      </button>

      <button type="button" class="legend-item clickable"
        :class="{ disabled: !visibleSeries.optimal }" @click="toggleSeries('optimal')" >
        <span class="swatch optimal-box"></span>
        <div>
          <div class="legend-label">Optimal Strain Range (Min – Max)</div>
          <div class="legend-sub">Recommended range for the day</div>
        </div>
      </button>

      <button type="button" class="legend-item clickable"
        :class="{ disabled: !visibleSeries.calories }" @click="toggleSeries('calories')" >
        <span class="swatch calories-box"></span>
        <div>
          <div class="legend-label">Calories (Kcal)</div>
          <div class="legend-sub">Daily calories burned - Avg: {{ avgCalories }} kcal</div>
        </div>
      </button>
    </div>
  </div>
</template>

<script>
import {
  Chart,
  BarController,
  BarElement,
  LineController,
  LineElement,
  PointElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend,
  Filler
} from "chart.js";

Chart.register(
  BarController,
  BarElement,
  LineController,
  LineElement,
  PointElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend,
  Filler
);

export default {
  name: "UserOptimalStrainChart",
  props: {
    labels: {
      type: Array,
      default: () => [
      ]
    },
    strainData: {
      type: Array,
      default: () => []
    },
    caloriesData: {
      type: Array,
      default: () => []
    },
    optimalMinData: {
      type: Array,
      default: () => []
    },
    optimalMaxData: {
      type: Array,
      default: () => []
    },
  },
  data() {
    return {
      chart: null,
      visibleSeries: {
        strain: true,
        optimal: true,
        calories: true,
        avgStrain: true,
        avgCalories: true
      },
      colours: {
        white: "#FFFFFF",
        strainLabel: "#f2e9e9",
        strainBar: "#d3abab",
        chartTitle: "#D8D9DA",
        optimalFillStyle: "rgba(191, 7, 242, 0.14)",
        optimalStrokeStyle: "rgba(191, 7, 242, 0.95)",
        optimalToolTip: "#bf07f2",
        calories: "#953535",
        strainColor: "#d9b6b8",
        ticksColor: "#9AA7A9",
        gridColor: "rgba(255,255,255,0.08)",


        darkRed: "#8B0000",
        red: "#FF0000",
        darkGray: "#283335",
        colorCalories: "#ff3a35",
        colorLimitline: "rgba(191, 7, 242, 0.95)"
      }
    };
  },
  computed: {
    avgStrain() {
      if (!this.strainData.length) return 0;
      const total = this.strainData.reduce((a, b) => a + b, 0);
      return Number(total / this.strainData.length).toFixed(1).replace(".0", "");
    },
    avgCalories() {
      if (!this.caloriesData.length) return 0;
      const total = this.caloriesData.reduce((a, b) => a + b, 0);
      return Number(total / this.caloriesData.length).toFixed(1);
    }
  },
  mounted() {
    this.renderChart();
    window.addEventListener("resize", this.handleResize);
  },
  beforeUnmount() {
    window.removeEventListener("resize", this.handleResize);
    if (this.chart) {
      this.chart.destroy();
      this.chart = null;
    }
  },
  watch: {
    labels: {
      deep: true,
      handler() {
        this.renderChart();
      }
    },
    strainData: {
      deep: true,
      handler() {
        this.renderChart();
      }
    },
    caloriesData: {
      deep: true,
      handler() {
        this.renderChart();
      }
    },
    optimalMinData: {
      deep: true,
      handler() {
        this.renderChart();
      }
    },
    optimalMaxData: {
      deep: true,
      handler() {
        this.renderChart();
      }
    },
    chartData: {
      deep: true,
      handler() {
        this.renderChart();
      }
    },
  },
  methods: {
    handleResize() {
      if (this.chart) this.chart.resize();
    },

    toggleSeries(key) {
      this.visibleSeries[key] = !this.visibleSeries[key];
      this.renderChart();
    },

    buildGradient(ctx, chartArea) {
      const gradient = ctx.createLinearGradient(0, chartArea.top, 0, chartArea.bottom);
      gradient.addColorStop(0, "rgba(239, 171, 170, 0.17)");
      gradient.addColorStop(1, "rgba(244, 117, 115, 0.17)");
      return gradient;
    },

    roundRectPath(ctx, x, y, width, height, radius) {
      const r = Math.min(radius, width / 2, height / 2);
      ctx.beginPath();
      ctx.moveTo(x + r, y);
      ctx.lineTo(x + width - r, y);
      ctx.quadraticCurveTo(x + width, y, x + width, y + r);
      ctx.lineTo(x + width, y + height - r);
      ctx.quadraticCurveTo(x + width, y + height, x + width - r, y + height);
      ctx.lineTo(x + r, y + height);
      ctx.quadraticCurveTo(x, y + height, x, y + height - r);
      ctx.lineTo(x, y + r);
      ctx.quadraticCurveTo(x, y, x + r, y);
      ctx.closePath();
    },

    createAvgLineLabelPlugin() {
      const vm = this;

      return {
        id: "avgLineLabelPlugin",
        afterDatasetsDraw(chart) {
          const { ctx, chartArea, scales } = chart;
          const yStrain = scales.y;
          const yCalories = scales.y1;

          if (!chartArea || !yStrain || !yCalories) return;

          ctx.save();

          const drawLabel = (text, x, y) => {
            const paddingX = 10;
            const boxHeight = 30;
            ctx.font = "600 12px Inter, Arial, sans-serif";
            const textWidth = ctx.measureText(text).width;
            const boxWidth = textWidth + paddingX * 2;

            ctx.fillStyle = "rgba(0, 0, 0, 0.72)";
            vm.roundRectPath(ctx, x - boxWidth, y - boxHeight / 2, boxWidth, boxHeight, 8);
            ctx.fill();

            ctx.fillStyle = vm.colours.white;
            ctx.textAlign = "left";
            ctx.textBaseline = "middle";
            ctx.fillText(text, x - boxWidth + paddingX, y);
          };

          if (vm.visibleSeries.avgCalories) {
            const avgCaloriesY = yCalories.getPixelForValue(Number(vm.avgCalories));
            drawLabel(`Avg Calories: ${vm.avgCalories} kcal`, chartArea.left + 165, avgCaloriesY);
          }

          if (vm.visibleSeries.avgStrain) {
            const avgStrainY = yStrain.getPixelForValue(Number(vm.avgStrain));
            drawLabel(`Avg Strain: ${vm.avgStrain}`, chartArea.right - 8, avgStrainY);
          }

          ctx.restore();
        }
      };
    },

    createCompositeOptimalPlugin() {
      const vm = this;

      return {
        id: "compositeOptimalPlugin",
        afterDatasetsDraw(chart) {
          if (!vm.visibleSeries.optimal) return;

          const { ctx, scales } = chart;
          const xScale = scales.x;
          const yScale = scales.y;
          if (!xScale || !yScale) return;

          const strainDatasetIndex = chart.data.datasets.findIndex(
            (d) => d.datasetKey === "strain"
          );
          if (strainDatasetIndex === -1) return;

          const strainMeta = chart.getDatasetMeta(strainDatasetIndex);

          ctx.save();

          vm.labels.forEach((_, index) => {
            const min = vm.optimalMinData[index];
            const max = vm.optimalMaxData[index];
            const strain = vm.strainData[index];

            const element = strainMeta.data[index];
            if (!element) return;

            const props = element.getProps(["x", "width"], true);
            const xCenter = props.x;
            const strainWidth = props.width;
            const optimalWidth = Math.max(12, strainWidth * 0.55);

            const x = xCenter - optimalWidth / 2;
            const yTop = yScale.getPixelForValue(max);
            const yBottom = yScale.getPixelForValue(min);
            const height = yBottom - yTop;

            // hanging optimal bar
            ctx.fillStyle = vm.colours.optimalFillStyle;
            vm.roundRectPath(ctx, x, yTop, optimalWidth, height, 8);
            ctx.fill();

            ctx.setLineDash([5, 4]);
            ctx.lineWidth = 1.5;
            ctx.strokeStyle = vm.colours.optimalStrokeStyle;
            vm.roundRectPath(ctx, x, yTop, optimalWidth, height, 8);
            ctx.stroke();
            ctx.setLineDash([]);

            // min/max dots
            ctx.fillStyle = vm.colours.optimalStrokeStyle;
            const dotX = xCenter;
            [yTop, yBottom].forEach((y) => {
              ctx.beginPath();
              ctx.arc(dotX, y, 4, 0, Math.PI * 2);
              ctx.fill();
            });

            // subtle cap inside actual strain only if optimal overlaps actual range
            if (vm.visibleSeries.strain) {
              const overlapTopValue = Math.min(max, strain);
              const overlapBottomValue = min;

              if (overlapTopValue > overlapBottomValue) {
                const overlapTop = yScale.getPixelForValue(overlapTopValue);
                const overlapBottom = yScale.getPixelForValue(overlapBottomValue);
                const overlapHeight = overlapBottom - overlapTop;

                ctx.fillStyle = "rgba(191, 7, 242, 0.10)";
                vm.roundRectPath(ctx, x, overlapTop, optimalWidth, overlapHeight, 6);
                ctx.fill();
              }
            }

            // optimal max/min labels
            ctx.fillStyle = vm.colours.optimalToolTip;
            ctx.font = "600 0.75rem";
            ctx.textAlign = "left";
            ctx.textBaseline = "middle";
            ctx.fillText(max.toFixed(1), xCenter - optimalWidth / 2 - 2, yTop - 10);
            ctx.fillText(min.toFixed(1), xCenter - optimalWidth / 2 - 2, yBottom + 12);
          });

          ctx.restore();
        }
      };
    },

    createStrainValuePlugin() {
      const vm = this;

      return {
        id: "strainValuePlugin",
        afterDatasetsDraw(chart) {
          if (!vm.visibleSeries.strain) return;

          const strainDatasetIndex = chart.data.datasets.findIndex(
            (d) => d.datasetKey === "strain"
          );
          if (strainDatasetIndex === -1) return;

          const { ctx } = chart;
          const meta = chart.getDatasetMeta(strainDatasetIndex);

          ctx.save();
          ctx.font = "600 12px Inter, Arial, sans-serif";
          ctx.textAlign = "center";
          ctx.textBaseline = "middle";

          meta.data.forEach((bar, index) => {
            const strain = vm.strainData[index];
            if (!strain || strain === 0) return; // skip zero values
            const props = bar.getProps(["x", "y", "width"], true);

            const label = String(strain.toFixed(1));
            const paddingX = 8;
            const boxHeight = 28;
            const textWidth = ctx.measureText(label).width;
            const boxWidth = textWidth + paddingX * 2;
            const boxX = props.x - boxWidth / 2;
            const boxY = props.y - 18;
            console.log('boxX: ', boxX);

            //ctx.fillStyle = "#F1F1F1";
            // ctx.fillStyle = 'rgba(30, 30, 40, 0.35)';
            // ctx.strokeStyle = 'rgba(255, 255, 255, 0.15)';
            // ctx.lineWidth = 1;

            // vm.roundRectPath(ctx, boxX, boxY, boxWidth, boxHeight, 6);
            // ctx.fill();

            ctx.fillStyle = vm.colours.strainLabel;
            ctx.fillText(label, props.x, boxY + boxHeight / 2.5);
          });

          ctx.restore();
        }
      };
    },

    renderChart() {
      const canvas = this.$refs.chartCanvas;
      if (!canvas) return;

      if (this.chart) {
        this.chart.destroy();
        this.chart = null;
      }

      const ctx = canvas.getContext("2d");
      if (!ctx) return;

      const avgLineLabelPlugin = this.createAvgLineLabelPlugin();
      const compositeOptimalPlugin = this.createCompositeOptimalPlugin();
      const strainValuePlugin = this.createStrainValuePlugin();

      let gradientStroke1 = ctx.createLinearGradient(0, 300, 0, 50);

      gradientStroke1.addColorStop(1, "rgba(172,0,0,0.15)");
      gradientStroke1.addColorStop(0, "transparent");

      this.chart = new Chart(ctx, {
        type: "bar",
        data: {
          labels: this.labels,
          datasets: [
            {
              type: "bar",
              datasetKey: "strain",
              label: "Strain",
              data: this.strainData,
              yAxisID: "y",
              hidden: !this.visibleSeries.strain,
              backgroundColor: this.colours.strainBar,
              borderColor: this.colours.strainBar,
              borderWidth: 1,
              borderRadius: 6,
              borderSkipped: false,
              barThickness: 34,
              order: 2
            },
            {
              type: "line",
              datasetKey: "calories",
              label: "Calories (Kcal)",
              data: this.caloriesData,
              yAxisID: "y1",
              hidden: !this.visibleSeries.calories,
              borderColor: this.colours.calories,
              pointBackgroundColor: this.colours.calories,
              pointBorderColor: this.colours.calories,
              backgroundColor: gradientStroke1,
              fill: true,
              pointRadius: 5,
              pointHoverRadius: 5,
              borderWidth: 3,
              tension: 0.38,
              order: 0
            },
            {
              type: "line",
              datasetKey: "avgStrain",
              label: "Avg Strain",
              data: new Array(this.labels.length).fill(Number(this.avgStrain)),
              yAxisID: "y",
              hidden: !this.visibleSeries.avgStrain,
              borderColor: "rgba(235, 200, 200, 0.65)",
              borderDash: [6, 6],
              borderWidth: 1.2,
              pointRadius: 0,
              fill: false,
              order: 0
            },
            {
              type: "line",
              datasetKey: "avgCalories",
              label: "Avg Calories",
              data: new Array(this.labels.length).fill(Number(this.avgCalories)),
              yAxisID: "y1",
              hidden: !this.visibleSeries.avgCalories,
              // borderColor: "rgba(255, 58, 53, 0.75)",
              borderColor: this.colours.calories,
              borderDash: [8, 6],
              borderWidth: 1.2,
              pointRadius: 0,
              fill: false,
              order: 0
            },
            {
            label: "Optimal",
            datasetKey: "optimalTooltip",
            data: this.labels.map(() => 0), // or this.strainData.map(() => 0)
            borderColor: "transparent",
            backgroundColor: "transparent",
            pointRadius: 0,
            pointHoverRadius: 0,
            borderWidth: 0,
            order: 999
          }
          ]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          animation: false,
          interaction: {
            mode: "index",
            intersect: false
          },
          layout: {
            padding: {
              top: 18,
              right: 28,
              bottom: 12,
              left: 12
            }
          },
          plugins: {
            legend: {
              display: false
            },
            tooltip: {
              backgroundColor: "rgba(12, 20, 24, 0.94)",
              titleColor: this.colours.white,
              bodyColor: "#d7dddd",
              borderColor: this.colours.gridColor,
              borderWidth: 1,
              padding: 12,
              displayColors: true,
              callbacks: {
                title: (items) => items[0]?.label?.replace("\n", ", "),
                label: (context) => {
                  const index = context.dataIndex;

                  if (context.dataset.datasetKey === "strain") {
                    return `Strain: ${this.strainData[index]}`;
                  }
                  if (context.dataset.datasetKey === "calories") {
                    return `Calories (Kcal): ${this.caloriesData[index]}`;
                  }
                  if (context.dataset.datasetKey === "optimalTooltip") {
                    const min = this.optimalMinData[index];
                    const max = this.optimalMaxData[index];
                    return `Optimal: ${min} - ${max}`;
                  }
                  return null;
                },
                afterBody: (items) => {
                  const index = items[0]?.dataIndex;
                  if (index === undefined) return [];

                  const min = this.optimalMinData[index];
                  const max = this.optimalMaxData[index];
                  const strain = this.strainData[index];

                  let status = "Within optimal";
                  if (strain < min) status = "Below optimal";
                  else if (strain > max) status = "Above optimal";

                  const lines = [];
                  // if (this.visibleSeries.optimal) {
                  //   lines.push(`Optimal: ${min} - ${max}`);
                  // }
                  lines.push(`Status: ${status}`);
                  return lines;
                },
                labelColor: (context) => {
                  if (context.dataset.datasetKey === "calories") {
                    return {
                      borderColor: this.colours.calories,
                      backgroundColor: this.colours.calories,
                      borderWidth: 2,
                      borderRadius: 2
                    };
                  }
                  if (context.dataset.datasetKey === "strain") {
                    return {
                      borderColor: this.colours.strainColor,
                      backgroundColor: this.colours.strainColor,
                      borderWidth: 2,
                      borderRadius: 2
                    };
                  }
                  if (context.dataset.datasetKey === "optimalTooltip") {
                    return {
                      borderColor: this.colours.optimalToolTip,
                      backgroundColor: this.colours.optimalToolTip,
                      borderWidth: 2,
                      borderRadius: 2
                    };
                  }

                  return {
                    borderColor: "transparent",
                    backgroundColor: "transparent",
                    borderWidth: 0
                  };
                },
              }
            }
          },
          scales: {
            x: {
              offset: true,
              grid: {
                color: "rgba(255,255,255,0.06)",
                drawBorder: false
              },
              ticks: {
                color: this.colours.ticksColor,
                padding: 16,
                font: {
                  size: 11
                },
                callback(value) {
                  const label = this.getLabelForValue(value);
                  return String(label).split("\n");
                }
              }
            },
            y: {
              position: "left",
              min: 0,
              max: 22,
              title: {
                display: true,
                text: "Strain",
                color: this.colours.chartTitle,
                font: {
                  size: 12,
                  weight: "600"
                }
              },
              grid: {
                color: this.colours.gridColor,
                drawBorder: false
              },
              ticks: {
                color: this.colours.ticksColor,
                stepSize: 2
              }
            },
            y1: {
              position: "right",
              min: 0,
              max: Math.ceil(Math.max(...this.caloriesData) * 1.1),
              title: {
                display: true,
                text: "Calories (Kcal)",
                color: this.colours.chartTitle,
                font: {
                  size: 12,
                  weight: "600"
                }
              },
              grid: {
                drawOnChartArea: false,
                drawBorder: false
              },
              ticks: {
                color: this.colours.ticksColor,
                callback(value) {
                  return Number(value).toLocaleString();
                }
              }
            }
          }
        },
        plugins: [avgLineLabelPlugin, compositeOptimalPlugin, strainValuePlugin]
      });
    }
  }
};
</script>

<style lang="scss" scoped>
.strain-calories-card {
  width: 100%;
  background: #283335;
  border: 1px solid #283335;
  border-radius: 16px;
  padding: 18px 18px 14px;
  color: #fff;
  box-sizing: border-box;
}

.chart-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
  flex-wrap: wrap;
}

.chart-title {
  margin: 0;
  font-size: 18px;
  font-weight: 700;
  letter-spacing: 0.4px;
  color: #f4f6f7;
}

.legend-pills {
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
}

.legend-pill {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 10px 14px;
  border: 1px solid rgba(191, 7, 242, 0.12);
  border-radius: 8px;
  color: #f3f4f4;
  font-size: 0.8125rem;
  //background: rgba(0, 0, 0, 0.08);
  cursor: pointer;
}

.legend-pill:hover {
  background: rgba(191, 7, 242, 0.04);
}

.legend-line {
  display: inline-block;
  width: 22px;
  height: 0;
  border-top-width: 2px;
  border-top-style: dashed;
}

.calories-line {
  border-top-color: #d3abab;
}

.strain-line {
  border-top-color: rgba(235, 200, 200, 0.85);
}

.chart-wrap {
  position: relative;
  width: 100%;
  height: 560px;
}

.bottom-legend {
    margin-top: 14px;
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 1rem;
    padding: 1rem;
    margin-left: 5rem;
    margin-right: 5rem;
    border-radius: 12px;
    background: rgba(0, 0, 0, 0.35);
    border: 1px solid rgba(191, 7, 242, 0.08);
}

.legend-item {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  min-width: 0;
  border: 0;
  background: transparent;
  color: inherit;
  text-align: left;
  padding: 0;
}

.legend-item.clickable {
  cursor: pointer;
}

.legend-item.disabled {
  opacity: 0.45;
}

.legend-label {
  font-size: 0.875rem;
  font-weight: 700;
  color: #eef1f2;
  line-height: 1.2;
}

.legend-sub {
  margin-top: 0.2rem;
  font-size: 0.75rem;
  color: #aeb9bb;
  line-height: 1.3;
}

.swatch {
  width: 0.3rem;
  height: 2rem;
  border-radius: 0.2rem;
  flex: 0 0 auto;
  margin-top: 0.1rem;
}

.strain-box {
  background: #d3abab;
  border: 1px solid rgba(239, 181, 194, 0.5);
}

.optimal-box {
  background: rgba(191, 7, 242, 0.14);
  border: 1.5px dashed rgba(191, 7, 242, 0.95);
}

.calories-box {
  width: 0.3rem;
  height: 2rem;
  background: #ff3a35;
  border: 1px solid #ff3a35;
}

.footer-note {
  margin-top: 0.875rem;
  color: #aeb9bb;
  font-size: 0.75rem;
  line-height: 1.4;
}

@media (max-width: 1100px) {
  .bottom-legend {
    grid-template-columns: 1fr;
  }

  .chart-wrap {
    height: 470px;
  }
}

@media (max-width: 640px) {
  .strain-calories-card {
    padding: 14px;
  }

  .chart-wrap {
    height: 410px;
  }

  .chart-title {
    font-size: 16px;
  }

  .legend-pill {
    font-size: 12px;
    padding: 8px 12px;
  }
  :export {
  darkRed: $g-red_900;
  red: $g-red_500;
  gray: $color-muted-text;
  darkGray: $color-gridlines;
  colorCalories: $g-red_100;
  colorStrain: $color-strain;
  colorAxes: $color-chart-axes-text;
  colorLimitline: $color-chart-limitline;
}
}
</style>